import React, { useState, useRef, useEffect } from "react";
import { cn } from "../../../lib/utils";
import { Check, ChevronDown, X } from "lucide-react";

interface GatewayMultiSelectProps {
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
}

export default function GatewayMultiSelect({
  options,
  values,
  onChange,
  placeholder = "All",
  className,
}: GatewayMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function toggle(opt: string) {
    if (values.includes(opt)) {
      onChange(values.filter((v) => v !== opt));
    } else {
      onChange([...values, opt]);
    }
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  const display =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? values[0]
        : `${values.length} selected`;

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-between gap-2 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs text-foreground/80 outline-none transition-colors",
          open ? "border-primary/50" : "hover:border-primary/30"
        )}
      >
        <span className={cn("truncate", values.length === 0 && "text-muted-foreground")}>
          {display}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {values.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearAll}
              className="text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg py-1">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground text-center">No options</div>
          ) : (
            options.map((opt) => {
              const selected = values.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors",
                    selected
                      ? "bg-primary/10 text-primary"
                      : "text-foreground/80 hover:bg-primary/[0.025]"
                  )}
                >
                  <span
                    className={cn(
                      "shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors",
                      selected ? "bg-primary border-primary" : "border-muted-foreground/40"
                    )}
                  >
                    {selected && <Check className="w-2.5 h-2.5 text-white" />}
                  </span>
                  <span className="truncate">{opt}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
